import React, { useState } from 'react'
import { FaUser, FaEnvelope, FaExclamationCircle, FaPhone, FaUserCheck, FaEnvelopeOpen, FaPhoneAlt } from 'react-icons/fa'
import './contactUsForm.css'
import SectionHeader from '../../Components/SectionHeader/SectionHeader'
import FeaturesCardCarousel from '../../Components/FeaturesCardCarousal/FeatureCardCarousal'
import imgquality from '../../assets/bestquality.png'
import imgMark from '../../assets/mark.png'
import imgTag from '../../assets/Xcode.png'
import imgVan from '../../assets/van.png'
import imgWarehouse from '../../assets/Warehouse.png'
import { useTranslation } from 'react-i18next';
import WhatsAppChat from '../../Components/WhatsAppChat/WhatsAppChat'
import FeaturesCard from '../../Components/FeaturesCard/FeaturesCard'
import { Col, Row } from 'react-bootstrap'


const ContactUsForm = ({ source = 'contact-us' }) => {
    const MAX_MESSAGE_LENGTH = 1000
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        telephone: '',
        cpfCnpj: '',
        consumption: false,
        resale: false,
        industrialization: false,
        message: ''
    })
    const [errors, setErrors] = useState({})
    const [touched, setTouched] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitSuccess, setSubmitSuccess] = useState(false)

    // Format telephone number (Brazilian format)
    const formatTelephone = (value) => {
        const numbers = value.replace(/\D/g, '')
        if (numbers.length <= 10) {
            return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
        } else {
            return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
        }
    }

    // Format CPF/CNPJ
    const formatCpfCnpj = (value) => {
        const numbers = value.replace(/\D/g, '')
        if (numbers.length <= 11) {
            // CPF format: XXX.XXX.XXX-XX
            return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
        } else {
            // CNPJ format: XX.XXX.XXX/XXXX-XX
            return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
        }
    }

    const validateField = (name, value) => {
        let error = ''

        switch (name) {
            case 'fullName':
                if (!value.trim()) {
                    error = 'Full name is required'
                } else if (value.trim().length < 2) {
                    error = 'Full name must be at least 2 characters'
                }
                break
            case 'email':
                if (!value.trim()) {
                    error = 'Email is required'
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    error = 'Please enter a valid email address'
                }
                break
            case 'telephone':
                if (!value.trim()) {
                    error = 'Telephone is required'
                } else if (value.replace(/\D/g, '').length < 10) {
                    error = 'Please enter a valid telephone number'
                }
                break
            case 'cpfCnpj':
                if (!value.trim()) {
                    error = 'CPF/CNPJ is required'
                } else {
                    const numbers = value.replace(/\D/g, '')
                    if (numbers.length !== 11 && numbers.length !== 14) {
                        error = 'CPF must have 11 digits or CNPJ must have 14 digits'
                    }
                }
                break
            case 'message':
                if (!value.trim()) {
                    error = 'Message is required'
                } else if (value.trim().length < 10) {
                    error = 'Message must be at least 10 characters'
                } else if (value.length > MAX_MESSAGE_LENGTH) {
                    error = `Message must not exceed ${MAX_MESSAGE_LENGTH} characters`
                }
                break
            default:
                break
        }

        return error
    }

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target
        let processedValue = value

        // Format telephone and CPF/CNPJ
        if (name === 'telephone') {
            processedValue = formatTelephone(value)
        } else if (name === 'cpfCnpj') {
            processedValue = formatCpfCnpj(value)
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : processedValue
        }))

        // Validate on change if field has been touched
        if (touched[name]) {
            const error = validateField(name, processedValue)
            setErrors(prev => ({
                ...prev,
                [name]: error
            }))
        }
    }

    const handleBlur = (e) => {
        const { name, value } = e.target
        setTouched(prev => ({ ...prev, [name]: true }))

        const error = validateField(name, value)
        setErrors(prev => ({
            ...prev,
            [name]: error
        }))
    }

    const validateForm = () => {
        const newErrors = {}
        const newTouched = {}

        Object.keys(formData).forEach(key => {
            if (key !== 'consumption' && key !== 'resale' && key !== 'industrialization') {
                newTouched[key] = true
                const error = validateField(key, formData[key])
                if (error) {
                    newErrors[key] = error
                }
            }
        })

        setTouched(newTouched)
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        setIsSubmitting(true)
        setSubmitSuccess(false)

        try {
            // Log form data to console with detailed information
            console.log('========================================')
            console.log('📧 Contact Form Submission Started')
            console.log('========================================')
            console.log('Form Data:', {
                fullName: formData.fullName,
                email: formData.email,
                telephone: formData.telephone,
                cpfCnpj: formData.cpfCnpj,
                consumption: formData.consumption,
                resale: formData.resale,
                industrialization: formData.industrialization,
                message: formData.message
            })
            console.log('Timestamp:', new Date().toISOString())
            console.log('Form is now LOCKED - All inputs disabled')
            console.log('========================================')

            // API endpoint:
            // - For multi-form routing, we hit the campaign endpoint.
            // - You can override base URL via REACT_APP_API_BASE (recommended per environment).
            const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.hansa-flex.co.in'
            const API_URL = `${API_BASE}/api/hf-brazil/contact/${source}`

            // Send form data to backend
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    fullName: formData.fullName,
                    email: formData.email,
                    telephone: formData.telephone,
                    cpfCnpj: formData.cpfCnpj,
                    consumption: formData.consumption,
                    resale: formData.resale,
                    industrialization: formData.industrialization,
                    message: formData.message,
                    source
                })
            })

            const data = await response.json()

            if (!response.ok) {
                throw new Error(data.error || 'Failed to send email')
            }

            // Log success
            console.log('✅ Form submission processed successfully')
            console.log('✅ Auto-reply email sent to:', formData.email)
            console.log('Form remains locked for review')
            console.log('========================================')

            // Show success message
            setSubmitSuccess(true)

            // Reset form after showing success
            setTimeout(() => {
                resetForm()
                setSubmitSuccess(false)
                setIsSubmitting(false)
                console.log('🔓 Form unlocked and reset')
            }, 5000)
        } catch (error) {
            console.error('❌ Error submitting form:', error)
            setErrors(prev => ({
                ...prev,
                submit: error.message || 'Failed to send email. Please try again later.'
            }))
            setIsSubmitting(false)
        }
    }
    const resetForm = () => {
        setFormData({
            fullName: '',
            email: '',
            telephone: '',
            cpfCnpj: '',
            consumption: false,
            resale: false,
            industrialization: false,
            message: ''
        })
        setTouched({})
        setErrors({})
    }

    const featuresData = [
        {
            title: "+55 (47) 3321-6300",
            description: "Telephone",
            image: imgMark
        },
        // {
        //     title: "0800 042 0112",
        //     description: "MHS - Mobile plumbing service - 24 hours",
        //     image: imgMark
        // },
        {
            title: "bbl@hansa-flex.com",
            description: "Email",
            image: imgMark
        }
    ]

    const featuresData2 = [
        {
            title: "Highest quality",
            description: "We uphold the highest quality standards and are pleased to confirm this to our customers through our certifications.",
            image: imgquality,
            cardclicklink: "/certifications"
        },
        {
            title: "X-CODE",
            description: "Keep an eye on the assembled hoses and reduce unplanned machine downtime.",
            image: imgTag,
            cardclicklink: "/x-code-manager"
        },
        {
            title: "MHS",
            description: "Our assembly teams are always ready with fully equipped workshop vehicles.",
            image: imgVan,
            cardclicklink: "/mobile-rapid-hydraulic-service"
        },
        {
            title: "Everything in one place.",
            description: "Take advantage of our complete line of hoses, tubes, and components for your projects, machines, and hydraulic systems.",
            image: imgWarehouse,
            cardclicklink: "/hydraulic-hoses"
        }
    ]



    return (
        <div >
            <div>
                <SectionHeader paragraph="We're here to help. Do you have any questions? Fill out the contact form." />
                <br />
                {/* <FeaturesCardCarousel
                    featuresData={featuresData}
                /> */}

                {/* <Row className='d-flex justify-content-between'>
                    <Col xs={12} md={6} lg={6}>
                        <FeaturesCard title="+55 (47) 3321-6300" description="Telephone" image={imgMark} />
                    </Col>
                    <Col xs={12} md={6} lg={6}>
                        <FeaturesCard title="bbl@hansa-flex.com" description="Email" image={imgMark} />
                    </Col>
                </Row> */}
            </div>

            <div className='contact-form-container'>
                <h1 className="contact-form-title">{t('Contact Form')}</h1>
                <p className="contact-form-instruction">{t('Please fill out the form below')}</p>
                <form className="contact-form" onSubmit={handleSubmit} style={{ pointerEvents: isSubmitting ? 'none' : 'auto', opacity: isSubmitting ? 0.7 : 1 }}>
                    <div className="form-group">
                        <div className="input-wrapper">
                            <input
                                type="text"
                                name="fullName"
                                placeholder={t('Full Name*')}
                                value={formData.fullName}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={`form-input ${errors.fullName ? 'input-error' : ''} ${touched.fullName && !errors.fullName && formData.fullName ? 'input-valid' : ''}`}
                                aria-label="Full Name"
                                aria-required="true"
                                aria-invalid={!!errors.fullName}
                                disabled={isSubmitting}
                                autoFocus
                            />
                            <FaUser className="input-icon" />
                            {touched.fullName && !errors.fullName && formData.fullName && (
                                <FaUserCheck className="input-icon-valid" />
                            )}
                        </div>
                        {errors.fullName && (
                            <span className="error-message" role="alert">
                                <FaExclamationCircle className="error-icon" />
                                {t(errors.fullName)}
                            </span>
                        )}
                    </div>

                    <div className="form-group">
                        <div className="input-wrapper">
                            <input
                                type="email"
                                name="email"
                                placeholder={t('Email*')}
                                value={formData.email}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={`form-input ${errors.email ? 'input-error' : ''} ${touched.email && !errors.email && formData.email ? 'input-valid' : ''}`}
                                aria-label="Email Address"
                                aria-required="true"
                                aria-invalid={!!errors.email}
                                disabled={isSubmitting}
                            />
                            <FaEnvelope className="input-icon" />
                            {touched.email && !errors.email && formData.email && (
                                <FaEnvelopeOpen className="input-icon-valid" />
                            )}
                        </div>
                        {errors.email && (
                            <span className="error-message" role="alert">
                                <FaExclamationCircle className="error-icon" />
                                {t(errors.email)}
                            </span>
                        )}
                    </div>

                    <div className="form-group">
                        <div className="input-wrapper">
                            <input
                                type="tel"
                                name="telephone"
                                placeholder={t('Telephone*')}
                                value={formData.telephone}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={`form-input ${errors.telephone ? 'input-error' : ''} ${touched.telephone && !errors.telephone && formData.telephone ? 'input-valid' : ''}`}
                                aria-label="Telephone"
                                aria-required="true"
                                aria-invalid={!!errors.telephone}
                                disabled={isSubmitting}
                            />
                            <FaPhone className="input-icon" />
                            {touched.telephone && !errors.telephone && formData.telephone && (
                                <FaPhoneAlt className="input-icon-valid" />
                            )}
                        </div>
                        {errors.telephone && (
                            <span className="error-message" role="alert">
                                <FaExclamationCircle className="error-icon" />
                                {t(errors.telephone)}
                            </span>
                        )}
                    </div>

                    <div className="form-group">
                        <div className="input-wrapper">
                            <input
                                type="text"
                                name="cpfCnpj"
                                placeholder={t('CPF/CNPJ*')}
                                value={formData.cpfCnpj}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                className={`form-input ${errors.cpfCnpj ? 'input-error' : ''} ${touched.cpfCnpj && !errors.cpfCnpj && formData.cpfCnpj ? 'input-valid' : ''}`}
                                aria-label="CPF or CNPJ"
                                aria-required="true"
                                aria-invalid={!!errors.cpfCnpj}
                                disabled={isSubmitting}
                            />
                        </div>
                        {errors.cpfCnpj && (
                            <span className="error-message" role="alert">
                                <FaExclamationCircle className="error-icon" />
                                {t(errors.cpfCnpj)}
                            </span>
                        )}
                    </div>

                    <div className="purchase-intent-section">
                        <p className="purchase-intent-question">{t('Do you wish to purchase HANSA-FLEX products for:')}</p>
                        <div className="checkbox-group" role="group" aria-label="Purchase intent">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="consumption"
                                    checked={formData.consumption}
                                    onChange={handleInputChange}
                                    className="checkbox-input"
                                    disabled={isSubmitting}
                                />
                                <span className="checkbox-text">{t('Consumption')}</span>
                            </label>

                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="resale"
                                    checked={formData.resale}
                                    onChange={handleInputChange}
                                    className="checkbox-input"
                                    disabled={isSubmitting}
                                />
                                <span className="checkbox-text">{t('Resale')}</span>
                            </label>

                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="industrialization"
                                    checked={formData.industrialization}
                                    onChange={handleInputChange}
                                    className="checkbox-input"
                                    disabled={isSubmitting}
                                />
                                <span className="checkbox-text">{t('Industrialization')}</span>
                            </label>
                        </div>
                    </div>

                    <div className="form-group message-group">
                        <textarea
                            name="message"
                            placeholder={t('Message*')}
                            value={formData.message}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            maxLength={MAX_MESSAGE_LENGTH}
                            className={`form-textarea ${errors.message ? 'input-error' : ''} ${touched.message && !errors.message && formData.message ? 'input-valid' : ''} ${formData.message.length >= MAX_MESSAGE_LENGTH ? 'char-limit-reached' : ''}`}
                            rows="6"
                            aria-label="Your Message"
                            aria-required="true"
                            aria-invalid={!!errors.message}
                            disabled={isSubmitting}
                        />
                        <div className="textarea-footer">
                            {errors.message && (
                                <span className="error-message" role="alert">
                                    <FaExclamationCircle className="error-icon" />
                                    {t(errors.message)}
                                </span>
                            )}

                        </div>
                    </div>

                    {errors.submit && (
                        <div className="error-message" style={{ marginTop: '10px', marginBottom: '10px' }} role="alert">
                            <FaExclamationCircle className="error-icon" />
                            {t(errors.submit)}
                        </div>
                    )}

                    <button
                        type="submit"
                        className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
                        disabled={isSubmitting}
                        aria-label="Submit contact form"
                    >
                        {isSubmitting ? t('Sending...') : t('Send')}
                    </button>
                </form>
                {submitSuccess && (
                    <div className="success-message" role="status" style={{ marginTop: '10px' }}>
                        {t('Message sent successfully')}
                    </div>
                )}
            </div>

            <div>
                <SectionHeader title="Your advantages with HANSA-FLEX" /></div>
            {/* <div className="contact-advantages-grid-wrapper"> */}
            <FeaturesCardCarousel
                featuresData={featuresData2}
                layout="grid"
                columns={4}
            />
            {/* </div> */}
            <WhatsAppChat phoneNumber="+55 (11) 95813-9550" />
        </div>
    )
}

export default ContactUsForm
